/**
 * «Помощник покупок» (ТЗ §7.6) — загрузка SDK cdp-web-chat (`createChatClient`,
 * `createChatWidget`).
 *
 * storefront-ui НЕ тянет `@cdp/web-messenger` как npm-зависимость (в монорепо нет
 * npm workspaces — соседние пакеты хостятся как собранные статические ассеты и
 * грузятся по URL рантаймом, см. web-tools-demo/server.mjs:
 * `chatSdkUrl: '/cdp-web-chat/index.js'`). Здесь тот же паттерн: собранный
 * self-contained ESM-бандл (vue+centrifuge уже забандлены, никаких bare-импортов —
 * см. cdp-web-chat/vite.config.ts, build.lib без rollupOptions.external) вендорится
 * в `public/cdp-web-chat/index.js` (тот же приём, что уже используется для
 * `public/cdp-web.global.js`) и грузится динамическим `import()`.
 *
 * Путь конфигурируется (`VITE_CHAT_SDK_URL`) на случай, если деплой решит отдавать
 * бандл откуда-то ещё (напр. отдельный CDN/движок) без пересборки storefront-ui.
 */

export interface ChatClientLike {
  init(options?: { chatInitToken?: string; experienceId?: string }): Promise<unknown>;
  sendMessage(text: string): Promise<unknown>;
  destroy?(): void;
}

export interface ChatWidgetLike {
  open(): void;
  setTheme?(theme: "light" | "dark" | "system"): void;
  /** Открыть существующую CDP-коллекцию в помощнике без изменения корзины. */
  openCollection?(collectionId: string): Promise<boolean>;
  close(): void;
  destroy?(): void;
}

export interface ChatWidgetOptionsLike {
  container: HTMLElement;
  client: ChatClientLike;
  mode?: "launcher" | "inline" | "fullscreen";
  /** Не рисовать стандартную кнопку чата, когда сайт предоставляет собственный графический вход. */
  showLauncher?: boolean;
  shadowDom?: boolean;
  title?: string;
  /** Штатная подмена текстов SDK для персонажа магазина. */
  texts?: { chat?: {
    assistant?: string;
    typing?: (name: string) => string;
    typingEllipsis?: (name: string) => string;
  } };
  persistVisibility?: boolean;
  /** Окно по центру почти на весь экран, сайт приглушён (клик по затемнению/Esc — свернуть). */
  modal?: boolean;
  /** Кнопка «Мои наборы» в шапке помощника. */
  mySets?: boolean;
  /** Кнопка-щит «Мои ограничения» в шапке помощника (у старых бандлов не показывается). */
  constraints?: boolean;
  /** Кнопка «Что я умею» в шапке помощника. */
  capabilities?: boolean;
  /** Кнопка витрины коллекций магазина в шапке помощника (подпись и значок — настройка сайта). */
  collectionBrowser?: { label: string; icon?: string };
  /** Телефон: результаты помощника в ленте разговора. */
  phoneInline?: boolean;
  /** «Наговорить в поле»: запись → текст в поле для правки. */
  voiceDictation?: boolean;
  /** Переопределение настроек оформления канала (только отображение). */
  presentationConfig?: Record<string, unknown>;
  /** Корзина изменилась в помощнике — обновить счётчик корзины сайта (у старых бандлов не вызывается). */
  onCartChange?: (cart: { version: number; itemsCount: number; totalKopecks: number }) => void;
}

export interface ChatClientConfigLike {
  baseUrl: string;
  writeKey: string;
  chatInitToken?: string;
  /** Experience панели чата (тема/название на сервере) — без него движок не применяет её PresentationBranding. */
  experienceId?: string;
  storageNamespace?: string;
  theme?: "light" | "dark" | "system";
}

/** Реестр рендеров рабочей области виджета (ядро); вертикаль регистрирует в нём свои карточки. */
export type PanelRendererRegistryLike = object;

export interface ChatSdkModule {
  createChatClient(config: ChatClientConfigLike): ChatClientLike;
  createChatWidget(options: ChatWidgetOptionsLike): ChatWidgetLike;
  /** Реестр рендеров (есть у сборок виджета с вертикалями; у старых бандлов — нет). */
  panelRenderers?: PanelRendererRegistryLike;
}

/** Модуль вертикали виджета «продукты» (`grocery.js`): кулинарный вид рецепта, плана и книги рецептов. */
export interface GroceryVerticalModule {
  registerGroceryRenderers(registry: PanelRendererRegistryLike): void;
}

function sdkUrl(): string {
  const injected = import.meta.env?.VITE_CHAT_SDK_URL;
  const url = injected && injected.trim() !== "" ? injected : "/cdp-web-chat/index.js";
  return versionedSdkUrl(url, typeof __CHAT_SDK_VERSION__ === "string" ? __CHAT_SDK_VERSION__ : "");
}

/** Both entrypoints use the same build digest; their shared chunks already have content-hashed names. */
export function versionedSdkUrl(url: string, version: string): string {
  if (!version) return url;
  const [path, fragment] = url.split("#", 2);
  return `${path}${path.includes("?") ? "&" : "?"}v=${encodeURIComponent(version)}${fragment ? `#${fragment}` : ""}`;
}

/** Вертикаль лежит рядом с ядром (одна сборка, общие чанки): `<каталог index.js>/grocery.js`. */
export function verticalUrl(coreUrl: string, name: string): string {
  const [path, query] = coreUrl.split("?", 2);
  const slash = path.lastIndexOf("/");
  return `${slash >= 0 ? path.slice(0, slash + 1) : ""}${name}.js${query ? `?${query}` : ""}`;
}

type Importer = (url: string) => Promise<unknown>;

/**
 * Vite dev adds `?import` to relative dynamic-import requests. Public assets must be requested as plain ESM,
 * otherwise the dev server rejects them before the browser can execute the SDK. An absolute same-origin URL is
 * treated as a native browser import in dev and remains the same file in production.
 */
export function absoluteSdkImportUrl(url: string, base: string): string {
  return new URL(url, base).href;
}
const dynamicImport: Importer = (url) => import(/* @vite-ignore */ absoluteSdkImportUrl(url, globalThis.location?.href ?? import.meta.url));

/**
 * Ядро виджета + вертикаль «продукты». Отдельная функция (не инлайн в store) — чтобы юнит-тесты могли подменить
 * загрузчик, не мокая динамический import() рантаймом URL. Вертикаль не загрузилась или бандл старый (без реестра) —
 * ядро работает само, рисуя карточки коллекций универсально (ТЗ core-vertical-boundary Г6а).
 */
export async function loadChatSdk(importer: Importer = dynamicImport): Promise<ChatSdkModule> {
  const url = sdkUrl();
  const mod = (await importer(url)) as ChatSdkModule;
  if (mod.panelRenderers) {
    try {
      const grocery = (await importer(verticalUrl(url, "grocery"))) as GroceryVerticalModule;
      grocery.registerGroceryRenderers(mod.panelRenderers);
    } catch (error) {
      if (typeof console !== "undefined") console.warn("[storefront] вертикаль виджета не загрузилась — карточки в универсальном виде", error);
    }
  }
  return mod;
}
